import {Extension} from "@tiptap/react";
import {Plugin, PluginKey, TextSelection} from "@tiptap/pm/state";
import {Decoration, DecorationSet} from "@tiptap/pm/view";
import {Node as PMNode} from "@tiptap/pm/model";

const MACRO_PATTERN = /\{([a-zA-Z0-9_]+)\}/g;

const macroPluginKey = new PluginKey<DecorationSet>("telegramMacros");

const buildDecorations = (doc: PMNode) => {
    const decorations: Decoration[] = [];

    doc.descendants((node, pos) => {
        if (!node.isTextblock) return true;

        const text = node.textBetween(0, node.content.size, undefined, "\n");

        for (const match of Array.from(text.matchAll(MACRO_PATTERN))) {
            const from = pos + 1 + match.index!;
            decorations.push(
                Decoration.inline(from, from + match[0].length, {class: "tg-macro"})
            );
        }
        return false;
    });

    return DecorationSet.create(doc, decorations);
};

export const TelegramMacros = Extension.create({
    name: "telegramMacros",
    priority: 1000,

    addKeyboardShortcuts() {
        return {
            Backspace: ({editor}) => {
                const {state} = editor;
                const {from, empty} = state.selection;
                if (!empty || from + 1 > state.doc.content.size) return false;

                const before = state.doc.textBetween(from - 1, from);
                const after = state.doc.textBetween(from, from + 1);
                if (before === "{" && after === "}") {
                    return editor.commands.deleteRange({from: from - 1, to: from + 1});
                }
                return false;
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin<DecorationSet>({
                key: macroPluginKey,
                state: {
                    init: (_, {doc}) => buildDecorations(doc),
                    apply: (tr, old) => (tr.docChanged ? buildDecorations(tr.doc) : old),
                },
                props: {
                    decorations: state => macroPluginKey.getState(state),

                    handleTextInput: (view, from, to, text) => {
                        const {state} = view;

                        if (text === "{") {
                            const tr = state.tr;
                            if (from !== to) {
                                tr.insertText("}", to).insertText("{", from);
                                tr.setSelection(TextSelection.create(tr.doc, from + 1, to + 1));
                            } else {
                                tr.insertText("{}", from);
                                tr.setSelection(TextSelection.create(tr.doc, from + 1));
                            }
                            view.dispatch(tr);
                            return true;
                        }

                        if (
                            text === "}" &&
                            from === to &&
                            from + 1 <= state.doc.content.size &&
                            state.doc.textBetween(from, from + 1) === "}"
                        ) {
                            view.dispatch(
                                state.tr.setSelection(TextSelection.create(state.doc, from + 1))
                            );
                            return true;
                        }

                        return false;
                    },
                },
            }),
        ];
    },
});

export const highlightMacros = (html: string): string =>
    html.replace(MACRO_PATTERN, '<span class="tg-macro">$&</span>');